import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";

import RevenueChart from "./components/RevenueChart";
import JobCompletionChart from "./components/JobCompletionChart";
import DonutChart from "./components/DonutChart";
import ClientRevenueChart from "./components/ClientRevenueChart";
import { reportsApi, paymentsApi } from "@/services/api";
import { exportToCSV, exportToExcel, exportToPDF } from "@/utils/export";
import { useRoleGuard, ROLE_GROUPS } from "@/hooks/useRoleGuard";
import { usePagePermission } from "@/hooks/usePagePermission";

type Period = "This Month" | "Last 3 Months" | "This Year";
type Section = "revenue" | "jobs" | "employees" | "clients" | "payments";

// ── Fallback mock data ────────────────────────────────────────────────────────
const fallbackRevenueData = [
  { month: "Oct", revenue: 185000, collected: 185000 },
  { month: "Nov", revenue: 220000, collected: 210000 },
  { month: "Dec", revenue: 195000, collected: 195000 },
  { month: "Jan", revenue: 310000, collected: 290000 },
  { month: "Feb", revenue: 275000, collected: 260000 },
  { month: "Mar", revenue: 534100, collected: 534100 },
  { month: "Apr", revenue: 436700, collected: 100300 },
];

const fallbackServiceData = [
  { name: "Topographic Survey", count: 12, revenue: 480000, color: "#10b981" },
  { name: "DGPS Survey", count: 8, revenue: 320000, color: "#f59e0b" },
  { name: "Boundary Survey", count: 6, revenue: 195000, color: "#6366f1" },
  { name: "Drone Survey", count: 4, revenue: 280000, color: "#ec4899" },
  { name: "Revenue Survey", count: 5, revenue: 245000, color: "#14b8a6" },
  { name: "Soil Test", count: 3, revenue: 90000, color: "#f97316" },
  { name: "As-Built Survey", count: 4, revenue: 160000, color: "#8b5cf6" },
  { name: "Hydrogeological", count: 2, revenue: 120000, color: "#06b6d4" },
];

const fallbackClientData = [
  { name: "HMDA Projects Ltd.", jobs: 8, revenue: 389400, paid: 389400 },
  { name: "NHAI Contractors", jobs: 5, revenue: 212400, paid: 0 },
  { name: "Telangana Housing Corp.", jobs: 4, revenue: 289100, paid: 289100 },
  { name: "Prestige Estates", jobs: 3, revenue: 76700, paid: 0 },
  { name: "Pharma City Dev. Corp.", jobs: 2, revenue: 259600, paid: 0 },
  { name: "AP State Roads Corp.", jobs: 2, revenue: 95000, paid: 0 },
];

const fallbackMonthlyJobData = [
  { month: "Oct", completed: 4, pending: 2, delivered: 3 },
  { month: "Nov", completed: 5, pending: 3, delivered: 4 },
  { month: "Dec", completed: 3, pending: 4, delivered: 3 },
  { month: "Jan", completed: 6, pending: 2, delivered: 5 },
  { month: "Feb", completed: 5, pending: 3, delivered: 4 },
  { month: "Mar", completed: 7, pending: 1, delivered: 6 },
  { month: "Apr", completed: 2, pending: 5, delivered: 1 },
];

// Fallback monthly payments by mode
const PAYMENT_MODE_COLORS: Record<string, string> = {
  NEFT:   "#10b981",
  RTGS:   "#f59e0b",
  UPI:    "#6366f1",
  Cheque: "#ec4899",
  Cash:   "#14b8a6",
  IMPS:   "#f97316",
  DD:     "#8b5cf6",
};

const fallbackPaymentTrend = [
  { month: "Oct", NEFT: 185000, RTGS: 0,      UPI: 0,     Cheque: 0,     Cash: 0 },
  { month: "Nov", NEFT: 120000, RTGS: 90000,  UPI: 0,     Cheque: 0,     Cash: 0 },
  { month: "Dec", NEFT: 95000,  RTGS: 100000, UPI: 0,     Cheque: 0,     Cash: 0 },
  { month: "Jan", NEFT: 150000, RTGS: 140000, UPI: 0,     Cheque: 0,     Cash: 0 },
  { month: "Feb", NEFT: 110000, RTGS: 150000, UPI: 45000, Cheque: 0,     Cash: 0 },
  { month: "Mar", NEFT: 289100, RTGS: 245000, UPI: 0,     Cheque: 45000, Cash: 0 },
  { month: "Apr", NEFT: 100300, RTGS: 0,      UPI: 76700, Cheque: 35000, Cash: 0 },
];

// ── Types ─────────────────────────────────────────────────────────────────────
interface RevenuePoint { month: string; revenue: number; collected: number; }
interface ServiceItem { name: string; count: number; revenue: number; color: string; }
interface ClientItem { name: string; jobs: number; revenue: number; paid: number; }
interface JobMonthItem { month: string; completed: number; pending: number; delivered: number; }
interface PaymentTrendPoint { month: string; [mode: string]: number | string; }

interface ReportsSummary {
  totalRevenue: number;
  totalCollected: number;
  totalPending: number;
  activeJobs: number;
  completedJobs: number;
  totalJobs: number;
  avgUtilization: number;
  collectionRate: number;
  updatedAt?: string;
}

const SERVICE_COLORS = ["#10b981","#f59e0b","#6366f1","#ec4899","#14b8a6","#f97316","#8b5cf6","#06b6d4"];

// ── Payments Trend Bar Chart ──────────────────────────────────────────────────
function PaymentsTrendChart({ data, modes }: { data: PaymentTrendPoint[]; modes: string[] }) {
  const maxVal = Math.max(...data.map((d) => modes.reduce((s, m) => s + (Number(d[m]) || 0), 0)));
  return (
    <div className="w-full">
      <div className="flex items-end gap-2 h-48">
        {data.map((d) => {
          const total = modes.reduce((s, m) => s + (Number(d[m]) || 0), 0);
          return (
            <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${maxVal > 0 ? (total / maxVal) * 160 : 0}px`, minHeight: total > 0 ? "4px" : "0" }}>
                {modes.map((mode) => {
                  const val = Number(d[mode]) || 0;
                  const pct = total > 0 ? (val / total) * 100 : 0;
                  return pct > 0 ? (
                    <div
                      key={mode}
                      title={`${mode}: ₹${(val / 1000).toFixed(0)}K`}
                      style={{ height: `${pct}%`, background: PAYMENT_MODE_COLORS[mode] || "#94a3b8", minHeight: "2px" }}
                    />
                  ) : null;
                })}
              </div>
              <span className="text-xs text-gray-500">{d.month}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  // Dynamic permission check — reads from localStorage saved by Admin Roles page
  // Default: managers and above (matches original behaviour)
  const { hasPermission: canViewReports, user: reportUser, loading: reportPermLoading } = usePagePermission("Reports", "view", false);

  // Still need useRoleGuard to ensure user is authenticated
  const { user: authUser } = useRoleGuard([]);
  const user = authUser;

  const [searchParams, setSearchParams] = useSearchParams();

  const [period, setPeriod] = useState<Period>("This Month");
  const [activeSection, setActiveSection] = useState<Section>(() => {
    const s = searchParams.get("section");
    const valid: Section[] = ["revenue", "jobs", "employees", "clients", "payments"];
    return valid.includes(s as Section) ? (s as Section) : "revenue";
  });
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>("April 7, 2026");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Live data state
  const [summary, setSummary] = useState<ReportsSummary | null>(null);
  const [revenueData, setRevenueData] = useState<RevenuePoint[]>(fallbackRevenueData);
  const [serviceData, setServiceData] = useState<ServiceItem[]>(fallbackServiceData);
  const [clientData, setClientData] = useState<ClientItem[]>(fallbackClientData);
  const [monthlyJobData, setMonthlyJobData] = useState<JobMonthItem[]>(fallbackMonthlyJobData);
  const [employeeData, setEmployeeData] = useState<{ id: number; name: string; role: string; utilization: number; activeJobs: number; completedJobs: number; status: string; avatar: string }[]>([]);
  const [paymentTrend, setPaymentTrend] = useState<PaymentTrendPoint[]>(fallbackPaymentTrend);
  const [paymentModes, setPaymentModes] = useState<string[]>(["NEFT", "RTGS", "UPI", "Cheque"]);

  const periodParam = period === "This Month" ? "month" : period === "Last 3 Months" ? "3months" : "year";

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, revenueRes, jobsRes, employeesRes, clientsRes, servicesRes, paymentsRes] = await Promise.allSettled([
        reportsApi.getSummary(periodParam),
        reportsApi.getRevenue(periodParam),
        reportsApi.getJobCompletion(periodParam),
        reportsApi.getEmployeeUtilization(),
        reportsApi.getClientAnalysis(),
        reportsApi.getServiceBreakdown(),
        paymentsApi.getAll({ limit: 200 }),
      ]);

      if (summaryRes.status === "fulfilled" && summaryRes.value) {
        setSummary(summaryRes.value);
        if (summaryRes.value.updatedAt) {
          setLastUpdated(new Date(summaryRes.value.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }));
        }
      }
      if (revenueRes.status === "fulfilled" && revenueRes.value?.data) setRevenueData(revenueRes.value.data);
      if (jobsRes.status === "fulfilled" && jobsRes.value?.data) setMonthlyJobData(jobsRes.value.data);
      if (employeesRes.status === "fulfilled" && employeesRes.value?.data) setEmployeeData(employeesRes.value.data);
      if (clientsRes.status === "fulfilled" && clientsRes.value?.data) setClientData(clientsRes.value.data);
      if (servicesRes.status === "fulfilled" && servicesRes.value?.data) {
        const colored = (servicesRes.value.data as ServiceItem[]).map((s: ServiceItem, i: number) => ({
          ...s, color: s.color || SERVICE_COLORS[i % SERVICE_COLORS.length],
        }));
        setServiceData(colored);
      }
      // Build payment trend from live data
      if (paymentsRes.status === "fulfilled") {
        const payments = Array.isArray(paymentsRes.value) ? paymentsRes.value : paymentsRes.value?.data ?? [];
        if (payments.length > 0) {
          const modesSet = new Set<string>(payments.map((p: { paymentMode?: string; payment_mode?: string }) => p.paymentMode || p.payment_mode || "Other"));
          const modes = Array.from(modesSet) as string[];
          setPaymentModes(modes);
          // Group by month
          const monthMap: Record<string, Record<string, number>> = {};
          payments.forEach((p: { paymentDate?: string; payment_date?: string; paymentMode?: string; payment_mode?: string; amount: number; status?: string }) => {
            if (p.status === "failed" || p.status === "refunded") return;
            const date = p.paymentDate || p.payment_date || "";
            const month = date ? new Date(date).toLocaleString("en-IN", { month: "short" }) : "?";
            const mode = p.paymentMode || p.payment_mode || "Other";
            if (!monthMap[month]) monthMap[month] = {};
            monthMap[month][mode] = (monthMap[month][mode] || 0) + Number(p.amount);
          });
          const trend = Object.entries(monthMap).map(([month, vals]) => ({ month, ...vals }));
          if (trend.length > 0) setPaymentTrend(trend);
        }
      }
    } catch {
      // API offline — keep mock data
    } finally {
      setLoading(false);
    }
  }, [periodParam]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Derived values
  const totalRevenue = summary?.totalRevenue ?? 0;
  const totalCollected = summary?.totalCollected ?? 0;
  const totalPending = summary?.totalPending ?? 0;
  const completedJobs = summary?.completedJobs ?? 0;
  const activeJobs = summary?.activeJobs ?? 0;
  const totalJobs = summary?.totalJobs ?? 0;
  const avgUtilization = summary?.avgUtilization ?? (employeeData.length > 0 ? Math.round(employeeData.reduce((a, b) => a + b.utilization, 0) / employeeData.length) : 0);
  const collectionRate = summary?.collectionRate ?? (totalRevenue > 0 ? Math.round((totalCollected / totalRevenue) * 100) : 0);

  const jobStatusDonut = [
    { label: "In Progress", value: activeJobs, color: "#f59e0b" },
    { label: "Pending", value: Math.max(0, totalJobs - activeJobs - completedJobs), color: "#fcd34d" },
    { label: "Completed", value: completedJobs, color: "#d97706" },
    { label: "Delivered", value: 0, color: "#92400e" },
  ];

  const revenueDonut = [
    { label: "Collected", value: totalCollected, color: "#f59e0b" },
    { label: "Pending", value: totalPending, color: "#d97706" },
    { label: "Other", value: Math.max(0, totalRevenue - totalCollected - totalPending), color: "#fde68a" },
  ];

  const totalRevenueSeries = revenueData.reduce((a, b) => a + b.revenue, 0);
  const totalCollectedSeries = revenueData.reduce((a, b) => a + b.collected, 0);
  const totalServiceCount = serviceData.reduce((a, b) => a + b.count, 0);

  // Payment stats from live paymentTrend data
  const receivedPayments: { amount: number; paymentMode: string; status: string }[] = [];
  paymentTrend.forEach((d) => {
    paymentModes.forEach((mode) => {
      const val = Number(d[mode]) || 0;
      if (val > 0) receivedPayments.push({ amount: val, paymentMode: mode, status: "received" });
    });
  });
  const totalPaymentsReceived = receivedPayments.reduce((a, b) => a + b.amount, 0);
  const modeStats = paymentModes.map((mode) => {
    const modePayments = receivedPayments.filter((p) => p.paymentMode === mode);
    return { mode, count: modePayments.length, amount: modePayments.reduce((a, b) => a + b.amount, 0) };
  }).filter((m) => m.count > 0);

  const modeDonut = modeStats.map((m) => ({
    label: m.mode,
    value: m.amount,
    color: PAYMENT_MODE_COLORS[m.mode] || "#94a3b8",
  }));

  // ── Export helpers ────────────────────────────────────────────────────────
  const getExportRows = (): Record<string, unknown>[] => {
    if (activeSection === "revenue") {
      return revenueData.map((r) => ({
        Month: r.month,
        "Invoiced (₹)": r.revenue,
        "Collected (₹)": r.collected,
        "Collection Rate (%)": Math.round((r.collected / (r.revenue || 1)) * 100),
      }));
    }
    if (activeSection === "jobs") {
      return monthlyJobData.map((r) => ({
        Month: r.month,
        Completed: r.completed,
        Pending: r.pending,
        Delivered: r.delivered,
        Total: r.completed + r.pending + r.delivered,
      }));
    }
    if (activeSection === "payments") {
      return modeStats.map((m) => ({
        "Payment Mode": m.mode,
        Transactions: m.count,
        "Total Amount (₹)": m.amount,
        "Avg per Txn (₹)": m.count > 0 ? Math.round(m.amount / m.count) : 0,
        "Share (%)": Math.round((m.amount / (totalPaymentsReceived || 1)) * 100),
      }));
    }
    if (activeSection === "employees") {
      return employeeData.map((e) => ({
        Name: e.name,
        Role: e.role,
        "Utilization (%)": e.utilization,
        "Active Jobs": e.activeJobs,
        "Completed Jobs": e.completedJobs,
        Status: e.status,
      }));
    }
    if (activeSection === "clients") {
      return clientData.map((c) => ({
        Client: c.name,
        Jobs: c.jobs,
        "Total Billed (₹)": c.revenue,
        "Collected (₹)": c.paid,
        "Outstanding (₹)": c.revenue - c.paid,
        "Collection Rate (%)": Math.round((c.paid / (c.revenue || 1)) * 100),
      }));
    }
    return [];
  };

  const exportFilename = `reports-${activeSection}-${period.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}`;
  const exportTitle = `Reports — ${activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} (${period})`;

  const handleExportCSV = () => {
    exportToCSV(getExportRows(), exportFilename);
    setExportMenuOpen(false);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    await exportToExcel(getExportRows(), exportFilename, exportTitle);
    setExporting(false);
    setExportMenuOpen(false);
  };

  const handleExportPDF = () => {
    exportToPDF("reports-content", exportFilename, exportTitle);
    setExportMenuOpen(false);
  };

  // Super admins always have access — never block them regardless of permissions config
  const isSuperAdmin = reportUser
    ? ["super_admin", "superadmin", "Super Admin", "admin"].some(
        (r) => r.toLowerCase() === (reportUser.role || "").toLowerCase()
      )
    : false;

  if (!reportPermLoading && reportUser && !canViewReports && !isSuperAdmin) {
    return (
      <ERPLayout title="Reports & Analytics">
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-2-line text-3xl text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-sm text-gray-500 max-w-sm mb-1">
            Your role <strong className="text-gray-700">({reportUser.role})</strong> does not have permission to view <strong>Reports</strong>.
          </p>
          <p className="text-xs text-gray-400 mb-6">Contact your administrator to request access.</p>
          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
            <i className="ri-shield-keyhole-line" />
            Permissions managed in <strong className="text-gray-600 ml-1">Admin Panel → Roles &amp; Permissions</strong>
          </div>
        </div>
      </ERPLayout>
    );
  }

  return (
    <ERPLayout title="Reports & Analytics">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Reports &amp; Analytics</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Business performance overview · Updated {lastUpdated}
              {loading && <span className="ml-2 text-xs text-amber-500 inline-flex items-center gap-1"><i className="ri-loader-4-line animate-spin" />Refreshing...</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {(["This Month", "Last 3 Months", "This Year"] as Period[]).map((p) => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${period === p ? "bg-white text-amber-700" : "text-gray-500"}`}>
                  {p}
                </button>
              ))}
            </div>
            <button onClick={loadReports} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50">
              <span className="w-4 h-4 flex items-center justify-center"><i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} /></span>Refresh
            </button>

            {/* Export dropdown */}
            <div className="relative">
              <button
                onClick={() => setExportMenuOpen((v) => !v)}
                disabled={exporting}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={exporting ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} />
                </span>
                Export
                <i className="ri-arrow-down-s-line text-xs" />
              </button>
              {exportMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl z-20 overflow-hidden">
                  <button
                    onClick={handleExportCSV}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-text-line text-amber-600" /></span>
                    Export CSV
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-line text-amber-600" /></span>
                    Export Excel
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-red-500" /></span>
                    Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Revenue", value: `₹${(totalRevenue / 100000).toFixed(1)}L`, sub: "All invoices raised", icon: "ri-money-rupee-circle-line", color: "text-amber-700", bg: "bg-amber-50", trend: "+18%", up: true, bar: collectionRate, barColor: "bg-amber-500" },
            { label: "Collected", value: `₹${(totalCollected / 100000).toFixed(1)}L`, sub: `${collectionRate}% collection rate`, icon: "ri-bank-card-line", color: "text-amber-600", bg: "bg-amber-50", trend: "+12%", up: true, bar: collectionRate, barColor: "bg-amber-400" },
            { label: "Pending / Overdue", value: `₹${(totalPending / 100000).toFixed(1)}L`, sub: "Awaiting payment", icon: "ri-alarm-warning-line", color: "text-red-500", bg: "bg-red-50", trend: "-5%", up: false, bar: Math.round((totalPending / totalRevenue) * 100), barColor: "bg-red-400" },
            { label: "Active Jobs", value: activeJobs, sub: `${completedJobs} completed this year`, icon: "ri-file-list-3-line", color: "text-amber-600", bg: "bg-amber-50", trend: "+3", up: true, bar: Math.round((activeJobs / totalJobs) * 100), barColor: "bg-amber-400" },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${kpi.bg}`}>
                  <i className={`${kpi.icon} text-xl ${kpi.color}`} />
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${kpi.up ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-500"}`}>{kpi.trend}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{kpi.label}</p>
              <p className="text-xs text-gray-400 mt-0.5 mb-3">{kpi.sub}</p>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${kpi.barColor} transition-all`} style={{ width: `${Math.min(100, kpi.bar || 0)}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
          {([
            { key: "revenue",  label: "Revenue Trends",       icon: "ri-line-chart-line" },
            { key: "jobs",     label: "Job Completion",        icon: "ri-task-line" },
            { key: "payments", label: "Payments",              icon: "ri-money-rupee-circle-line" },
            { key: "employees",label: "Employee Utilization",  icon: "ri-team-line" },
            { key: "clients",  label: "Client Analysis",       icon: "ri-building-2-line" },
          ] as { key: Section; label: string; icon: string }[]).map((s) => (
            <button key={s.key} onClick={() => { setActiveSection(s.key); setSearchParams((prev) => { prev.set("section", s.key); return prev; }); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeSection === s.key ? "bg-white text-amber-700" : "text-gray-500 hover:text-gray-700"}`}>
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${s.icon} text-sm`} /></span>
              {s.label}
            </button>
          ))}
        </div>

        {/* All section content wrapped for PDF */}
        <div id="reports-content">
          {/* ── REVENUE SECTION ── */}
          {activeSection === "revenue" && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-5">
                <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-800">Monthly Revenue Trend</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Oct 2025 – Apr 2026</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" />Invoiced</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-300" />Collected</span>
                    </div>
                  </div>
                  <RevenueChart data={revenueData} />
                  <div className="mt-4 grid grid-cols-3 gap-3 border-t border-gray-100 pt-4">
                    {[
                      { label: "Total Invoiced", value: `₹${(totalRevenueSeries / 100000).toFixed(1)}L` },
                      { label: "Total Collected", value: `₹${(totalCollectedSeries / 100000).toFixed(1)}L` },
                      { label: "Collection Rate", value: `${Math.round((totalCollectedSeries / (totalRevenueSeries || 1)) * 100)}%` },
                    ].map((s) => (
                      <div key={s.label} className="text-center">
                        <p className="text-lg font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Collection Status</h3>
                    <div className="flex items-center justify-center mb-4">
                      <DonutChart data={revenueDonut} size={140} centerValue={`${collectionRate}%`} centerLabel="collected" />
                    </div>
                    <div className="space-y-2">
                      {revenueDonut.filter((d) => d.value > 0).map((d) => (
                        <div key={d.label} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                            <span className="text-xs text-gray-600">{d.label}</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-800">₹{(d.value / 1000).toFixed(0)}K</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-3">Top Month</h3>
                    <div className="text-center py-2">
                      {(() => {
                        const top = [...revenueData].sort((a, b) => b.revenue - a.revenue)[0];
                        return top ? (
                          <>
                            <p className="text-2xl font-bold text-amber-600">{top.month}</p>
                            <p className="text-xs text-gray-500 mt-1">₹{(top.revenue / 100000).toFixed(2)}L invoiced</p>
                            <p className="text-xs text-gray-400">{Math.round((top.collected / top.revenue) * 100)}% collected</p>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800">Revenue by Service Type</h3>
                  <span className="text-xs text-gray-400">{serviceData.length} service types</span>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {serviceData.map((s) => (
                    <div key={s.name}>
                      <div className="flex justify-between text-xs mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                          <span className="text-gray-600">{s.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400">{s.count} jobs</span>
                          <span className="font-semibold text-gray-800">₹{(s.revenue / 1000).toFixed(0)}K</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(s.revenue / (serviceData[0]?.revenue || 1)) * 100}%`, background: s.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── JOBS SECTION ── */}
          {activeSection === "jobs" && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-5">
                <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-800">Monthly Job Completion</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Stacked by status</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-600" />Completed</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400" />Pending</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-400" />Delivered</span>
                    </div>
                  </div>
                  <JobCompletionChart data={monthlyJobData} />
                  <div className="mt-4 grid grid-cols-4 gap-3 border-t border-gray-100 pt-4">
                    {[
                      { label: "Total Jobs", value: monthlyJobData.reduce((a, b) => a + b.completed + b.pending + b.delivered, 0) },
                      { label: "Completed", value: monthlyJobData.reduce((a, b) => a + b.completed, 0) },
                      { label: "Delivered", value: monthlyJobData.reduce((a, b) => a + b.delivered, 0) },
                      { label: "Avg/Month", value: Math.round(monthlyJobData.reduce((a, b) => a + b.completed + b.pending + b.delivered, 0) / (monthlyJobData.length || 1)) },
                    ].map((s) => (
                      <div key={s.label} className="text-center">
                        <p className="text-lg font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Current Status</h3>
                    <div className="flex items-center justify-center mb-4">
                      <DonutChart data={jobStatusDonut} size={140} centerValue={`${totalJobs}`} centerLabel="total jobs" />
                    </div>
                    <div className="space-y-2">
                      {jobStatusDonut.map((d) => (
                        <div key={d.label} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                            <span className="text-xs text-gray-600">{d.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-800">{d.value}</span>
                            <span className="text-xs text-gray-400">{Math.round((d.value / (totalJobs || 1)) * 100)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-3">Completion Rate</h3>
                    <div className="text-center py-2">
                      <p className="text-3xl font-bold text-amber-600">{Math.round((completedJobs / (totalJobs || 1)) * 100)}%</p>
                      <p className="text-xs text-gray-500 mt-1">{completedJobs} of {totalJobs} jobs done</p>
                    </div>
                    <div className="mt-3 h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${Math.round((completedJobs / (totalJobs || 1)) * 100)}%` }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800">Jobs by Service Type</h3>
                  <span className="text-xs text-gray-400">{totalServiceCount} total jobs</span>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  {serviceData.map((s) => (
                    <div key={s.name} className="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: s.color }} />
                        <span className="text-xs text-gray-400">{Math.round((s.count / (totalServiceCount || 1)) * 100)}%</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{s.count}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-tight">{s.name}</p>
                      <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(s.count / (serviceData[0]?.count || 1)) * 100}%`, background: s.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── PAYMENTS SECTION ── */}
          {activeSection === "payments" && (
            <div className="space-y-5">
              {/* KPI row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Total Received",  value: `₹${(totalPaymentsReceived / 100000).toFixed(1)}L`, icon: "ri-checkbox-circle-line", color: "text-amber-700", bg: "bg-amber-50" },
                  { label: "Transactions",    value: receivedPayments.length,                             icon: "ri-exchange-line",         color: "text-amber-600",    bg: "bg-amber-50" },
                  { label: "Avg per Txn",     value: `₹${receivedPayments.length > 0 ? Math.round(totalPaymentsReceived / receivedPayments.length).toLocaleString("en-IN") : 0}`, icon: "ri-bar-chart-line", color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Payment Modes",   value: modeStats.length,                                    icon: "ri-bank-card-line",        color: "text-amber-500",    bg: "bg-amber-50" },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${s.bg}`}>
                      <i className={`${s.icon} text-lg ${s.color}`} />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{s.value}</p>
                      <p className="text-xs text-gray-500">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart + Donut */}
              <div className="grid grid-cols-3 gap-5">
                <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-800">Monthly Payments by Mode</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Stacked bar — received payments only</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {paymentModes.map((m) => (
                        <span key={m} className="flex items-center gap-1.5 text-xs text-gray-600">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PAYMENT_MODE_COLORS[m] || "#94a3b8" }} />
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                  <PaymentsTrendChart data={paymentTrend} modes={paymentModes} />
                  <div className="mt-4 grid grid-cols-3 gap-3 border-t border-gray-100 pt-4">
                    {[
                      { label: "Peak Month", value: (() => { const top = [...paymentTrend].sort((a, b) => paymentModes.reduce((s, m) => s + (Number(b[m]) || 0), 0) - paymentModes.reduce((s, m) => s + (Number(a[m]) || 0), 0))[0]; return top?.month ?? "—"; })() },
                      { label: "Total Received", value: `₹${(totalPaymentsReceived / 100000).toFixed(1)}L` },
                      { label: "Top Mode", value: modeStats.sort((a, b) => b.amount - a.amount)[0]?.mode ?? "—" },
                    ].map((s) => (
                      <div key={s.label} className="text-center">
                        <p className="text-lg font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">By Payment Mode</h3>
                    {modeDonut.length > 0 ? (
                      <>
                        <div className="flex items-center justify-center mb-4">
                          <DonutChart data={modeDonut} size={140} centerValue={`${modeStats.length}`} centerLabel="modes" />
                        </div>
                        <div className="space-y-2">
                          {modeStats.sort((a, b) => b.amount - a.amount).map((m) => (
                            <div key={m.mode} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PAYMENT_MODE_COLORS[m.mode] || "#94a3b8" }} />
                                <span className="text-xs text-gray-600">{m.mode}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{m.count} txn</span>
                                <span className="text-xs font-semibold text-gray-800">₹{(m.amount / 1000).toFixed(0)}K</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-8">No payment data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Mode breakdown table */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-800">Payment Mode Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Mode</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Transactions</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total Amount</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Avg per Txn</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modeStats.sort((a, b) => b.amount - a.amount).map((m) => {
                        const share = Math.round((m.amount / (totalPaymentsReceived || 1)) * 100);
                        return (
                          <tr key={m.mode} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: PAYMENT_MODE_COLORS[m.mode] || "#94a3b8" }} />
                                <span className="text-sm font-medium text-gray-800">{m.mode}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center"><span className="text-sm text-gray-700">{m.count}</span></td>
                            <td className="px-4 py-3 text-right"><span className="text-sm font-semibold text-gray-800">₹{m.amount.toLocaleString("en-IN")}</span></td>
                            <td className="px-4 py-3 text-right"><span className="text-sm text-gray-600">₹{m.count > 0 ? Math.round(m.amount / m.count).toLocaleString("en-IN") : 0}</span></td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${share}%`, background: PAYMENT_MODE_COLORS[m.mode] || "#94a3b8" }} />
                                </div>
                                <span className="text-xs font-semibold text-gray-600 w-8 text-right">{share}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td className="px-5 py-3 text-sm font-bold text-gray-800">Total</td>
                        <td className="px-4 py-3 text-center text-sm font-bold text-gray-800">{receivedPayments.length}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">₹{totalPaymentsReceived.toLocaleString("en-IN")}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-gray-800">₹{receivedPayments.length > 0 ? Math.round(totalPaymentsReceived / receivedPayments.length).toLocaleString("en-IN") : 0}</td>
                        <td className="px-4 py-3" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── EMPLOYEES SECTION ── */}
          {activeSection === "employees" && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-5">
                <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="font-semibold text-gray-800">Employee Utilization</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Current workload per team member</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" />&lt;70%</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400" />70–89%</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400" />&ge;90%</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {employeeData.map((emp) => (
                      <div key={emp.id} className="flex items-center gap-4">
                        <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{emp.avatar}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center mb-1.5">
                            <div>
                              <span className="text-sm font-medium text-gray-800">{emp.name}</span>
                              <span className="text-xs text-gray-400 ml-2">{emp.role}</span>
                            </div>
                            <span className={`text-xs font-bold ml-2 flex-shrink-0 ${emp.utilization >= 90 ? "text-red-500" : emp.utilization >= 70 ? "text-amber-600" : "text-amber-500"}`}>{emp.utilization}%</span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${emp.utilization >= 90 ? "bg-red-400" : emp.utilization >= 70 ? "bg-amber-400" : "bg-amber-300"}`} style={{ width: `${emp.utilization}%` }} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 w-20">
                          <p className="text-xs font-semibold text-gray-700">{emp.activeJobs} active</p>
                          <p className="text-xs text-gray-400">{emp.completedJobs} done</p>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${emp.status === "Active" ? "bg-amber-50 text-amber-700" : emp.status === "On Leave" ? "bg-orange-50 text-orange-600" : "bg-gray-100 text-gray-500"}`}>{emp.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Utilization Overview</h3>
                    <div className="flex items-center justify-center mb-4">
                      <DonutChart data={[
                        { label: "Overloaded", value: employeeData.filter((e) => e.utilization >= 90).length, color: "#f87171" },
                        { label: "High", value: employeeData.filter((e) => e.utilization >= 70 && e.utilization < 90).length, color: "#f59e0b" },
                        { label: "Normal", value: employeeData.filter((e) => e.utilization < 70).length, color: "#fcd34d" },
                      ]} size={140} centerValue={`${avgUtilization}%`} centerLabel="avg util." />
                    </div>
                    <div className="space-y-2">
                      {[
                        { label: "Overloaded (≥90%)", count: employeeData.filter((e) => e.utilization >= 90).length, color: "text-red-500", bg: "bg-red-50" },
                        { label: "High (70–89%)", count: employeeData.filter((e) => e.utilization >= 70 && e.utilization < 90).length, color: "text-amber-600", bg: "bg-amber-50" },
                        { label: "Normal (<70%)", count: employeeData.filter((e) => e.utilization < 70).length, color: "text-amber-500", bg: "bg-amber-50" },
                      ].map((s) => (
                        <div key={s.label} className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">{s.label}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.color} ${s.bg}`}>{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-3">Team Summary</h3>
                    <div className="space-y-2.5">
                      {[
                        { label: "Total Employees", value: employeeData.length, icon: "ri-team-line" },
                        { label: "Active", value: employeeData.filter((e) => e.status === "Active").length, icon: "ri-user-follow-line" },
                        { label: "On Leave", value: employeeData.filter((e) => e.status === "On Leave").length, icon: "ri-user-unfollow-line" },
                        { label: "Total Active Jobs", value: employeeData.reduce((a, b) => a + b.activeJobs, 0), icon: "ri-file-list-3-line" },
                        { label: "Jobs Completed", value: employeeData.reduce((a, b) => a + b.completedJobs, 0), icon: "ri-checkbox-circle-line" },
                      ].map((s) => (
                        <div key={s.label} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center"><i className={`${s.icon} text-sm text-gray-400`} /></span>
                            <span className="text-xs text-gray-600">{s.label}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-800">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── CLIENTS SECTION ── */}
          {activeSection === "clients" && (
            <div className="space-y-5">
              {/* Client Revenue Bar Chart */}
              <ClientRevenueChart data={clientData} />

              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: "Total Clients", value: clientData.length, icon: "ri-building-2-line", color: "text-amber-700", bg: "bg-amber-50" },
                  { label: "Paying Clients", value: clientData.filter((c) => c.paid > 0).length, icon: "ri-user-follow-line", color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Outstanding", value: `₹${(clientData.reduce((a, b) => a + (b.revenue - b.paid), 0) / 100000).toFixed(1)}L`, icon: "ri-alarm-warning-line", color: "text-red-500", bg: "bg-red-50" },
                  { label: "Avg Revenue/Client", value: `₹${Math.round(clientData.reduce((a, b) => a + b.revenue, 0) / (clientData.length || 1) / 1000)}K`, icon: "ri-bar-chart-line", color: "text-amber-600", bg: "bg-amber-50" },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg}`}>
                      <i className={`${s.icon} text-lg ${s.color}`} />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{s.value}</p>
                      <p className="text-xs text-gray-500">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Client Revenue Analysis</h3>
                  <span className="text-xs text-gray-500">{clientData.length} clients</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Jobs</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total Billed</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Collected</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Outstanding</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Collection Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientData.map((c, i) => {
                        const outstanding = c.revenue - c.paid;
                        const rate = Math.round((c.paid / (c.revenue || 1)) * 100);
                        return (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <i className="ri-building-2-line text-gray-500 text-sm" />
                                </div>
                                <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center"><span className="text-sm font-medium text-gray-700">{c.jobs}</span></td>
                            <td className="px-4 py-3 text-right"><span className="text-sm font-semibold text-gray-800">₹{c.revenue.toLocaleString()}</span></td>
                            <td className="px-4 py-3 text-right"><span className="text-sm font-semibold text-amber-600">₹{c.paid.toLocaleString()}</span></td>
                            <td className="px-4 py-3 text-right"><span className={`text-sm font-semibold ${outstanding > 0 ? "text-red-500" : "text-gray-400"}`}>₹{outstanding.toLocaleString()}</span></td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${rate === 100 ? "bg-amber-500" : rate > 0 ? "bg-amber-400" : "bg-gray-200"}`} style={{ width: `${rate}%` }} />
                                </div>
                                <span className="text-xs font-semibold text-gray-600 w-8 text-right">{rate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td className="px-5 py-3 text-sm font-bold text-gray-800">Total</td>
                        <td className="px-4 py-3 text-center text-sm font-bold text-gray-800">{clientData.reduce((a, b) => a + b.jobs, 0)}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-gray-800">₹{clientData.reduce((a, b) => a + b.revenue, 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">₹{clientData.reduce((a, b) => a + b.paid, 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-red-500">₹{clientData.reduce((a, b) => a + (b.revenue - b.paid), 0).toLocaleString()}</td>
                        <td className="px-4 py-3" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Revenue Share by Client</h3>
                <div className="space-y-3">
                  {[...clientData].sort((a, b) => b.revenue - a.revenue).map((c, i) => {
                    const totalRev = clientData.reduce((a, b) => a + b.revenue, 0);
                    const pct = Math.round((c.revenue / (totalRev || 1)) * 100);
                    const colors = ["#f59e0b","#d97706","#b45309","#92400e","#fbbf24","#fcd34d"];
                    return (
                      <div key={c.name}>
                        <div className="flex justify-between text-xs mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ background: colors[i % colors.length] }} />
                            <span className="text-gray-700 font-medium">{c.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400">{c.jobs} jobs</span>
                            <span className="font-semibold text-gray-800">₹{(c.revenue / 1000).toFixed(0)}K</span>
                            <span className="text-gray-400 w-8 text-right">{pct}%</span>
                          </div>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: colors[i % colors.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ERPLayout>
  );
}
